import { HttpHeaders } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { apiUrls, LOADING_FOR_AUTHENTICATION_MS } from '@easy-lib/constants';
import { AccountService } from '@easy-lib/modules/account-activity';
import {
	AuthType,
	BaseService,
	InternetConnectionService,
	LocalStorageService,
	LsService,
	MarketSheetLsService,
	NotificationService,
	NotificationType,
	sKey,
	StartupService,
	UserDeviceService,
	UserService
} from '@easy-lib/modules/shared/services';
import { guid } from '@easy-lib/tools';
import { jwtDecode } from 'jwt-decode';
import { User, UserManager } from 'oidc-client-ts';
import { BehaviorSubject, Observable, Subject, tap, throwError } from 'rxjs';
import { catchError, first, take } from 'rxjs/operators';
import { AccesToken, LoginError } from '../models/auth.model';
import { getClientSettings } from '../settings/auth-settings';
import { GtagService } from '@easy-lib/modules/shared/services/gtag.service';

@Injectable({
	providedIn: 'root'
})
export class AuthService extends BaseService {
	private accountService: AccountService = inject(AccountService);
	private localStorage: LocalStorageService = inject(LocalStorageService);
	private startupService: StartupService = inject(StartupService);
	private notifyService: NotificationService = inject(NotificationService);
	private internetConnectivity: InternetConnectionService = inject(InternetConnectionService);
	private userService: UserService = inject(UserService);
	private lsService: LsService = inject(LsService);
	private marketsheetLsService: MarketSheetLsService = inject(MarketSheetLsService);
	private userDeviceService: UserDeviceService = inject(UserDeviceService);
	private router: Router = inject(Router);
	private gtagService: GtagService = inject(GtagService);

	public settings = getClientSettings(this.environment);
	public manager = new UserManager(this.settings);
	public waitingForLogin = true;
	checkUserFailedSub: Subject<boolean> = new Subject<boolean>();
	checkLoginSuccessSub: BehaviorSubject<LoginError> = new BehaviorSubject<LoginError>(LoginError.None);
	checkUserFailed$ = this.checkUserFailedSub.asObservable();
	checkLoginSuccess$ = this.checkLoginSuccessSub.asObservable();
	msgSenderUniqId: string;
	logOutFromThisTab = false;
	logOutForTokenExpiration = false;
	private user: User;

	constructor() {
		super();
		this.internetConnectivity.networkStatus$
			.pipe(
				take(1),
				tap(isConnected => {
					if (!isConnected) this.checkLoginSuccessSub.next(LoginError.InternetIsDisconnected);
					return;
				})
			)
			.subscribe();
		this.msgSenderUniqId = guid();
		setTimeout(() => {
			this.waitingForLogin = false;
		}, LOADING_FOR_AUTHENTICATION_MS);

		if (this.startupService.authenticationType === AuthType.Secondary) return;
		// After reload Page if user is logged in user checked should be called
		void this.manager.getUser().then(user => {
			this.user = user;
			this.accountService.setUserAccountInfo(this.accessToken);
			this.isAuthorizedUser();
			if (user && this.isLoggedIn()) {
				this.userChecked();
			} else if (user && this.userExpired) {
				this.logOutForTokenExpiration = true;
				this.logOutFromApp();
			}
		});

		this.manager.events.addUserLoaded(user => {
			this.user = user;
			this.accountService.setUserAccountInfo(this.accessToken);
			const isLoggedIn = this.localStorage.getItem(sKey.loggedIn);
			if (!isLoggedIn) this.localStorage.setItem(sKey.loggedIn, 'true');
			sessionStorage.removeItem(sKey.isGuestUser);
			this.isAuthorizedUser();
			if (this.isLoggedIn()) {
				this.sameLogin();
				let returnUrl = this.localStorage.getItem('redirect-after-login');
				if (!returnUrl) returnUrl = '/';

				const paramString = returnUrl.split('?')[1];
				if (paramString) {
					returnUrl = returnUrl.split('?')[0];
				}
				const queryString = new URLSearchParams(paramString);
				const params = {};
				queryString.forEach((value, key) => {
					params[key] = value;
				});

				void this.router.navigate([returnUrl], { queryParams: params });
			}
		});
	}

	get getUser() {
		return this.manager.getUser();
	}

	get easyApiUrl(): string {
		return this.mtsPath + apiUrls.easy;
	}

	private get userExpired() {
		const expires_in = this.user?.expires_at ? this.user?.expires_at - Date.now() / 1000 : undefined;
		if (expires_in !== undefined) {
			return expires_in <= 0;
		}
		return true;
	}
	private get accessToken(): AccesToken {
		const token = this.getToken();
		if (token) {
			const parsed = jwtDecode<AccesToken>(token);
			return parsed;
		}
		return null;
	}

	isLoggedIn(): boolean {
		const res = this.user != null && !this.userExpired;
		if (res) this.waitingForLogin = false;
		return res;
	}

	sameLogin() {
		this.msgSenderUniqId = guid();
		this.userDeviceService.UserDeviceInfo$.pipe(
			first(a => !!a),
			tap(a => {
				return this.httpService
					.post<any>(`${this.easyApiUrl}account/same-login`, {
						body: {
							uuid: this.msgSenderUniqId,
							...a
						},
						dontCheckSameLogin: true,
						maxRetryAttempts: 3
					})
					.pipe(tap(() => this.userChecked()))
					.subscribe();
			})
		).subscribe();
	}

	getToken() {
		if (this.user !== null) return this.user?.access_token;
		return null;
	}

	startAuthentication(): Promise<void> {
		this.waitingForLogin = true;

		if (this.startupService.authenticationType === AuthType.Secondary) {
			return this.router.navigateByUrl('/login').then(() => Promise.resolve());
		}
		return this.manager.signinRedirect();
	}

	logout() {
		sessionStorage.removeItem(sKey.isGuestUser);
		this.logOutFromThisTab = true;
		this.logoutOms().subscribe({
			next: () => {
				if (this.startupService.authenticationType === AuthType.Secondary) {
					this.router.navigateByUrl('/login').then(() => Promise.resolve());
				} else {
					if (this.user && this.isLoggedIn()) {
						this.logOutFromApp();
					} else {
						window.location.href = this.environment.appUrl;
					}
				}
			},
			error: () => {
				if (this.startupService.authenticationType === AuthType.Secondary) {
					this.router.navigateByUrl('/login').then(() => Promise.resolve());
				} else {
					if (this.user && this.isLoggedIn()) {
						this.logOutFromApp();
					} else {
						window.location.href = this.environment.appUrl;
					}
				}
			}
		});
	}
	logOutFromApp() {
		this.lsService.unsubscribeAllAndDisconnect();
		this.marketsheetLsService.unsubscribeAllAndDisconnect();
		this.localStorage.removeItem(sKey.loggedIn);
		this.manager.signoutRedirect().then(() => {
			this.manager.removeUser();
		});
	}
	completeAuthentication(): Promise<void> {
		return this.manager
			.signinRedirectCallback()
			.then(() => {
				this.gtagService.sendGtagEvent('redirect-to-app');
			})
			.catch(error => {
				let errorType = LoginError.General;
				this.logout();
				if (`${error}`.indexOf('iat is in the future') >= 0 || `${error}`.indexOf('exp is in the past') >= 0) {
					errorType = LoginError.DateError;
				}
				this.checkLoginSuccessSub.next(errorType);
			});
	}

	setLoginError(error: LoginError) {
		this.checkLoginSuccessSub.next(error);
	}

	redirectToSignIn() {
		this.manager.signinRedirect();
	}

	loginForPasswordGrantType(value): Observable<any> {
		this.localStorage.removeItem(sKey.loggedIn);
		sessionStorage.removeItem(sKey.isGuestUser);
		// this.localStorage.removeItem(this.jwtKey);
		const options = {
			headers: new HttpHeaders().set('Content-Type', 'application/x-www-form-urlencoded')
		};
		const body = new URLSearchParams();
		body.set('username', value.username);
		body.set('password', value.password);
		body.set('grant_type', 'password');
		body.set('client_id', this.environment.oidcSettings.client_id);

		return this.httpService
			.post<any>(this.environment.oidcSettings.authority + '/connect/token', {
				body: body.toString(),
				options: options
			})
			.pipe(
				tap(res => {
					const date = new Date();
					date.setSeconds(date.getSeconds() + res.expires_in);
					res.expires_at = +date;
					// this.localStorage.setItem(this.jwtKey, JSON.stringify(res));
					this.user = null;
					this.accountService.setUserAccountInfo(this.accessToken);
					const isLoggedIn = this.localStorage.getItem(sKey.loggedIn);
					if (!isLoggedIn) this.localStorage.setItem(sKey.loggedIn, 'true');
					// this.checkUserAccount();
					let returnUrl = this.localStorage.getItem('redirect-after-login');
					if (!returnUrl) returnUrl = '/';
					void this.router.navigate([returnUrl]);
					this.isAuthorizedUser();
					this.sameLogin();
				}),
				catchError(error => {
					if (error?.error?.error == 'invalid_grant')
						this.notifyService.alert(error?.error?.error_description, NotificationType.danger, false);
					return throwError(error);
				})
			);
	}
	redirectToLoginPage() {
		window.location.href = this.environment.appUrl;
	}
	private unsubscriber: Subject<void> = new Subject<void>();

	private userChecked() {
		if (!this.userService.getValue()) {
			this.userService.userChecked();
			this.checkUserFailedSub.next(false);
			this.waitingForLogin = false;
			// this.accountService.isGuestUser = false;
			// this.userDeviceSubject.subscribe((a) => {
			//   if (a === DeviceTypeEnum.Desktop) this.logOutOtherTabsAfterLogOut();
			// });
		}
	}
	private logoutOms() {
		// this.localStorage.setItem(this.jwtKey, JSON.stringify(this._user));
		return this.httpService.post<any>(`${this.easyApiUrl}account/logout`, null);
	}
	private isAuthorizedUser(): void {
		if (this.accessToken) {
			if (!this.accessToken.contract) {
				this.setLoginError(LoginError.GuestUser);
				this.userService.isGuestUser = true;
			}
			if (this.accessToken.contract && this.accessToken.contract.lastIndexOf('OmsMofid_1.0') < 0) {
				this.setLoginError(LoginError.Easy2User);
				this.userService.isGuestUser = true;
				this.userService.isEasy2User = true;
			}
			if (!this.hasEContract2() && this.isForceContract2()) {
				this.setLoginError(LoginError.ForceContract2);
			}
		}
		return null;
	}
	public hasEContract2(): boolean {
		return this.accessToken?.contract?.includes('Econtract_2.0');
	}
	public isForceContract2(): boolean {
		return this.accessToken?.isforce_contract === 'true';
	}
	public hasContractOptionBuy(): boolean {
		return this.accessToken?.contract?.includes('TradeOptionContract_1.0');
	}
	public hasContractOptionSell(): boolean {
		return this.accessToken?.contract?.includes('ShortSell_1.0');
	}

	private redirectToEasy2() {
		window.location.href = this.environment.setting.easy2ReferralUrl + window.location.pathname;
	}

	private logOutOtherTabsAfterLogOut() {
		setInterval(() => {
			const isLoggedIn = this.localStorage.getItem(sKey.loggedIn);
			let lastError = null;
			this.checkLoginSuccessSub.subscribe(a => (lastError = a));
			if (isLoggedIn && lastError === LoginError.LogedOutFromOtherTab) {
				this.manager.removeUser();
				location.reload();
			}
			if (!isLoggedIn) {
				this.manager.removeUser();
				sessionStorage.removeItem(sKey.isGuestUser);
				if (!this.logOutFromThisTab && !this.logOutForTokenExpiration) {
					this.checkLoginSuccessSub.next(LoginError.LogedOutFromOtherTab);
				}
			}
		}, 10000);
	}
}
